import { FormHelperText, MenuItem, useTheme } from '@mui/material'
import Card from '@mui/material/Card'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { Box, styled } from '@mui/system'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import {
  createAgreement,
  deleteAgreement,
  fetchAgreementPreview,
  fetchAgreementTemplateFormFields,
} from '../../api/agreements'
import { fetchTeamInviteOptions, inviteTeamMember } from '../../api/teams'
import { validateEmailText } from '../../helpers/validateEmail'
import { useAppSnackbar } from '../../hooks/useAppSnackbar'
import useDynamicFieldStates from '../../hooks/useDynamicFieldStates'
import { useEmailMessage } from '../../hooks/useEmailMessage'
import useLoadingContext from '../../hooks/useLoadingContext'
import { useLoadingIds } from '../../hooks/useLoadingIds'
import { useMountEffect } from '../../hooks/useMountEffect'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import useValidationMessages from '../../hooks/useValidationMessages'
import {
  AgreementDocumentSelectOption,
  AgreementTemplateFormField,
  AgreementTemplateFormFieldInputType,
  AgreementTemplateFormFields,
  CommunitySelectOptions,
  RoleAgreementDocumentSelectOption,
} from '../../swagger'
import { useUser } from '../../UserContext'
import { createSafeDate, dateToDashString } from '../../utils/dateUtility'
import { reinterpretYearMonthDayAsLocalTime } from '../../utils/reinterpretYearMonthDayAsLocalTime'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'
import TeamMemberButtons from '../Buttons/TeamMemberButtons'
import { TextButtonVariant } from '../Buttons/TextButton'
import CardFormHeader from '../Card/CardFormHeader'
import DateField, { DateFieldVariant } from '../Elements/DateField'
import { FormDivider } from '../Elements/FormDivider'
import Header, { HeaderVariant } from '../Elements/Header'
import LoadingProgress from '../Elements/LoadingProgress'
import { ProgramsCheckBoxSelector } from '../Elements/ProgramsCheckBoxSelector'
import DropDown, { DropDownVariant } from '../Menus/DropDown'
import ConfirmationModal from '../Modals/ConfirmationModal'
import FormData from '../Team/FormData'
import { StandardizedFieldNames } from '../Team/StandardizedFields'
import { buildAcademicYearsFilterOptions } from '../../helpers/buildAcademicYearsFilterOptions'
import { jsDateFromYearMonthDayString } from '../../utils/jsDateFromYearMonthDayString'

const Form = styled('form')(({ theme }) => ({
  '& .MuiTextField-root': {
    margin: theme.spacing(1),
    width: `calc(100% - ${theme.spacing(1)})`,
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1, 0),
    },
  },
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

const PublicUrlLink = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  marginLeft: theme.spacing(2),
}))

const SectionOne = styled('section')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: '50% 50%',
  gridTemplateRows: 'auto ',
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface TeamMemberCardProps {
  title: string
}

enum TeamMemberValidationMessageTypes {
  Default = 'default',
  NoResultsForRole = 'noResultsForRole',
  Role = 'role',
  AgreementTemplate = 'agreementTemplate',
  SignerEmail = 'signerEmail',
  NoRoles = 'noRoles',
  SignerEmailFormat = 'signerEmailFormat',
}

const initialApplicantInfo = { firstName: '', lastName: '' }

const TeamMemberCard: React.FC<TeamMemberCardProps> = (props) => {
  const { validationMessages } = useEmailMessage()
  const theme = useTheme()
  const { t } = useTranslation()
  const { title } = props

  const { handleError: handleAppError, handleMessage } = useAppSnackbar()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleError = useCallback(handleAppError, [])

  const {
    TeamMemberCard: {
      inviteTeamMember: inviteTeamMemberLoadingId,
      fetchInviteTeamMemberOptions: fetchInviteTeamMemberOptionsLoadingId,
    },
  } = useLoadingIds()

  const navigate = useNavigate()

  const showOnDesktop = useShowOnDesktop()

  const validationMessageMap = useValidationMessages([
    {
      field: TeamMemberValidationMessageTypes.Default,
      message: t(
        'Team.TeamMemberForm.ErrorMessage',
        'Something went wrong. Please make sure you have filled out the required fields.'
      ),
    },
    {
      field: TeamMemberValidationMessageTypes.NoResultsForRole,
      message: t(
        'Team.TeamMemberForm.NoResultsForRole',
        'No Results for selected Role.'
      ),
    },
    {
      field: TeamMemberValidationMessageTypes.Role,
      message: t(
        'Team.TeamMemberForm.RoleValidationMessage',
        'Role cannot be empty.'
      ),
    },

    {
      field: TeamMemberValidationMessageTypes.NoRoles,
      message: t(
        'Team.TeamMemberForm.NoRolesErrorMessage',
        'Something went wrong. No roles were found to start an agreement.'
      ),
    },
    {
      field: TeamMemberValidationMessageTypes.AgreementTemplate,
      message: t(
        'Team.TeamMemberForm.AgreementTemplateValidationMessage',
        'Agreement Template cannot be empty.'
      ),
    },
    {
      field: TeamMemberValidationMessageTypes.SignerEmail,
      message: t(
        'Team.TeamMemberForm.SignerEmailValidationMessage',
        'Email address cannot be empty.'
      ),
    },
    {
      field: TeamMemberValidationMessageTypes.SignerEmailFormat,
      message: t(
        'Team.TeamMemberForm.SignerEmailValidationMessage',
        'Email address is formatted incorrectly.'
      ),
    },
  ])

  const errorMessage = validationMessageMap.get(
    TeamMemberValidationMessageTypes.Default
  ) as string

  const [isLoading, setIsLoading] = useState(false)
  const [loadingFields, setLoadingFields] = useState(false)

  const [role, setRole] = useState<RoleAgreementDocumentSelectOption>()
  const [roleId, setRoleId] = useState(-1)
  const [bypassAgreement, setBypassAgreement] = useState(false)
  const [requiredEndDate, setRequiredEndDate] = useState(false)
  const [effectiveDate, setEffectiveDate] = useState(new Date())
  const [roleChanged, setRoleChanged] = useState(false)
  const [agreementTemplateChanged, setAgreementTemplateChanged] =
    useState(false)
  const [agreementTemplate, setAgreementTemplate] = useState('')
  const [signerEmail, setSignerEmail] = useState('')

  const [isRoleValid, setIsRoleValid] = useState(true)
  const [isAgreementDocumentValid, setIsAgreementDocumentValid] = useState(true)
  const [isSignerEmailValid, setIsSignerEmailValid] = useState(false)
  const [isFormValidated, setIsFormValidated] = useState(false)
  const [textErrorEmail, setTextErrorEmail] = useState<string>('')
  const [communitySelectOptions, setCommunitySelectOptions] = useState<
    CommunitySelectOptions[]
  >([])
  const [selectedCommunity, setSelectedCommunity] = useState({
    communityKey: '0',
    name: '',
  })
  const [agreementTemplateFormFields, setAgreementTemplateFormFields] =
    useState<AgreementTemplateFormFields | undefined>()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const [isApplicantInfoRequired, setIsApplicantInfoRequired] = useState(false)
  const [applicantInfo, setApplicantInfo] = useState(initialApplicantInfo)
  const [loadRoleAgreementDocuments, setLoadRoleAgreementDocuments] =
    useState(true)
  const [roleAgreementDocuments, setRoleAgreementDocuments] =
    useState<RoleAgreementDocumentSelectOption[]>()
  const [previousAgreementTemplateId, setPreviousAgreementTemplateId] =
    useState('')
  const [agreementTemplateId, setAgreementTemplateId] = useState('')
  const [previousAgreementDocumentId, setPreviousAgreementDocumentId] =
    useState('')
  const [agreementDocumentId, setAgreementDocumentId] = useState('')
  const [roleEndDate, setRoleEndDate] = useState(new Date())
  const [academicYears, setAcademicYears] =
    useState<{ year: number; name: string; isDefault: boolean }[]>()
  const [selectedAcademicYear, setSelectedAcademicYear] = useState({
    year: 0,
    name: '',
  })
  const [updateStandardizedFieldStates, setUpdateStandardizedFieldStates] =
    useState(false)
  const [isInitialRecipientLoad, setIsInitialRecipientLoad] = useState(true)
  const [isInitialFormDataLoad, setIsInitialFormDataLoad] = useState(true)
  const [isInitialStandardizedFieldLoad, setIsInitialStandardizedFieldLoad] =
    useState(true)
  const [agreementId, setAgreementId] = useState('')
  const [agreementPreview, setAgreementPreview] = useState('')
  const [formDataFieldNames, setFormDataFieldNames] = useState<string[]>([])
  const [, setRecipientFieldNames] = useState<string[]>([])

  const [selectedPrograms, setSelectedPrograms] = useState('')

  const clearFields = () => {
    setIsAgreementDocumentValid(true)
    setRecipientStates({})
    setIsInitialRecipientLoad(true)
    setFormDataStates({})
    setIsInitialFormDataLoad(true)
    setSignerEmail('')
    setIsFormValidated(false)
    setIsSignerEmailValid(false)
  }

  const handleRoleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const roleAgreementDocument = rolesMap.get(event.target.value)
    const selectedRoleBypassesAgreements =
      roleAgreementDocument?.bypassAutomatedAgreements ?? false
    setRole(roleAgreementDocument)
    setRoleId(roleAgreementDocument?.roleKey ?? roleId)
    setBypassAgreement(selectedRoleBypassesAgreements)
    setRequiredEndDate(roleAgreementDocument?.requireEndDate ?? false)
    setRoleChanged(true)
    setIsRoleValid(true)
    const firstAgreementTemplate = roleAgreementMap.get(event.target.value)
    setPreviousAgreementDocumentId(agreementDocumentId)
    setPreviousAgreementTemplateId(agreementTemplateId)
    const newAgreementTemplateId =
      firstAgreementTemplate?.templateId ?? agreementTemplateId
    const newAgreementDocumentId =
      firstAgreementTemplate?.agreementDocuments[0]?.agreementDocumentId ??
      agreementDocumentId
    setAgreementTemplate(
      firstAgreementTemplate?.agreementDocuments[0]?.agreementDocumentName ??
        agreementTemplate
    )
    setAgreementDocumentId(newAgreementDocumentId)
    setAgreementTemplateId(newAgreementTemplateId)
    if (
      selectedRoleBypassesAgreements ||
      newAgreementDocumentId !== agreementDocumentId ||
      newAgreementTemplateId !== agreementTemplateId
    )
      setAgreementTemplateFormFields(undefined)
    setUpdateStandardizedFieldStates(true)
    clearFields()
  }

  const handleAgreementTemplateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const [agreementTemplate, newAgreementDocumentId] =
      event.target.value.split('|-|')

    setPreviousAgreementDocumentId(agreementDocumentId)
    setPreviousAgreementTemplateId(agreementTemplateId)
    setAgreementTemplate(agreementTemplate)
    setAgreementDocumentId(newAgreementDocumentId)
    setIsAgreementDocumentValid(true)
    clearFields()
    setAgreementTemplateFormFields(undefined)
    setFormDataFieldNames([])
    setAgreementTemplateChanged(true)
    setUpdateStandardizedFieldStates(true)
  }

  const handleSignerEmailChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSignerEmail(event.target.value.trim())
  }

  const doRecipientsValidate = () => {
    const isInvalidEmail = validateEmailText(signerEmail)
    setTextErrorEmail(isInvalidEmail)
    const signerEmailValid = !isInvalidEmail
    setIsSignerEmailValid(signerEmailValid)
    return signerEmailValid
  }

  const isValidCommunity =
    selectedCommunity.communityKey !== '0' &&
    selectedCommunity.name !== '' &&
    selectedCommunity.name !== StandardizedFieldNames.Community

  const communityDefault = t(
    'Team.TeamMemberForm.CommunityDefaultMessage',
    `Select a {{label}}`,
    { label: StandardizedFieldNames.Community }
  )
  const communities = communitySelectOptions
    ? [
        {
          communityKey: 0,
          name: communityDefault,
        },
        ...communitySelectOptions,
      ]
    : []
  const communityOptions = communities.map(({ communityKey: id, name }) => {
    return { name, id }
  })

  const doesFormDataValidate = () => {
    let doesFormDataValidate = true
    let formDataStateObject = {}
    for (const [
      key,
      { state, required, maxLength, minLength, inputType },
    ] of Object.entries(formDataStates)) {
      const isDateField = inputType === AgreementTemplateFormFieldInputType.Date
      const isOverLength = !!maxLength && state.length > maxLength,
        isUnderLength = !!minLength && state.length < minLength,
        isProperLength = !isOverLength && !isUnderLength

      if (required && (!state || (!!state && !isProperLength))) {
        doesFormDataValidate = false
      }
      formDataStateObject = {
        ...formDataStateObject,
        [key]: {
          state,
          required,
          maxLength,
          minLength,
          inputType,
          isValid: required
            ? {
                input: !!state,
                maxLength: isDateField || !maxLength ? true : !isOverLength,
                minLength: isDateField || !minLength ? true : !isUnderLength,
                email:
                  key.trim() === 'Personal Email of Contractor'
                    ? !validateEmailText(state)
                    : true,
              }
            : { input: true, maxLength: true, minLength: true, email: true },
          validationMessage:
            key.trim() === 'Personal Email of Contractor' &&
            validationEmailMessage(validateEmailText(state)),
        },
      }
    }
    setFormDataStates({ ...formDataStates, ...formDataStateObject })
    return doesFormDataValidate
  }

  const isProgramsFieldIncluded = agreementTemplateFormFields?.formData?.some(
    (field) => field.inputName === StandardizedFieldNames.Programs
  )

  const showProgramsSelector =
    !loadingFields && !bypassAgreement && roleId > 0 && isProgramsFieldIncluded

  const isProgramsFieldValid = showProgramsSelector
    ? selectedPrograms.length > 0
    : true

  const fieldsDoValidate = () => {
    const recipientsValidate = doRecipientsValidate()
    const formDataValidates = doesFormDataValidate()

    setIsRoleValid(!!role)
    setIsAgreementDocumentValid(!!agreementTemplate)

    const shouldValidateCommunity =
      communitySelectOptions.length > 0 && isCommunityIncluded

    const communityValid = shouldValidateCommunity ? isValidCommunity : true

    const academicYearValid = requiredEndDate ? isValidAcademicYear : true

    return (
      !!role &&
      (bypassAgreement || !!agreementTemplate) &&
      recipientsValidate &&
      communityValid &&
      academicYearValid &&
      isProgramsFieldValid &&
      (bypassAgreement || formDataValidates)
    )
  }

  const removeSpacesStartAndEndFromEmailField = (
    inputName: string,
    value: string
  ): string => {
    // Add here all fields containing email to avoid entering spaces at the beginning and end of the field
    const emailTypeFields = [
      'Area Representative',
      'Sales Manager',
      'RecipientArea Representative',
      'Personal Email of Contractor',
      'Personal Email of Employee',
      'Support Rep',
      'Book Sales Manager',
    ]

    const exists = emailTypeFields.includes(inputName.trim())

    if (exists) {
      return value.trim()
    }
    return value
  }

  const isValidApplicantInfo = applicantInfo.firstName && applicantInfo.lastName

  // FIXME: Need to determine supervisorId, not just default to current user
  const { user } = useUser()

  //Enter the handle next before to validate the fields and call the endpoint to create an Agreement.
  const handleNext = async () => {
    setIsFormValidated(true)
    setIsLoading(true)
    if (fieldsDoValidate()) {
      try {
        const validFrom = jsDateFromYearMonthDayString(
          dateToDashString(effectiveDate)
        )
        const result = await createAgreement({
          roleId,
          agreementTemplateId, // This to be deprecated, the name agreementTemplateId is actually the agreementDocumentId (names got changed)
          agreementDocumentId,
          // FIXME: Some users, like admin, who can select roles cannot create agreements
          supervisorUserId: user?.id,
          signerEmail,
          validFrom,
          academicYear: selectedAcademicYear.year,
          recipients: [
            {
              label: 'Signer',
              email: signerEmail,
            },
          ],
          prefilledFormFields: formDataFieldNames.map((fieldId) => {
            if (
              fieldId.match(StandardizedFieldNames.EndDate) ||
              fieldId.match(StandardizedFieldNames.EndDate.split(' ').join(''))
            ) {
              return {
                fieldName: fieldId,
                fieldValue:
                  standardizedFieldStates[StandardizedFieldNames.EndDate]
                    ?.state,
              }
            }
            if (fieldId.match(StandardizedFieldNames.CommunityId)) {
              return {
                fieldName: fieldId,
                fieldValue: selectedCommunity.communityKey,
              }
            }
            if (fieldId.match(StandardizedFieldNames.CommunityName)) {
              return {
                fieldName: fieldId,
                fieldValue: selectedCommunity.name,
              }
            }
            if (fieldId.match(StandardizedFieldNames.Programs)) {
              return {
                fieldName: fieldId,
                fieldValue: selectedPrograms,
              }
            }
            return {
              fieldName: fieldId,
              fieldValue: formDataStates[fieldId]?.state ?? '',
            }
          }),
        })
        if (!!result) {
          setAgreementId(result.agreementId)
          setIsApplicantInfoRequired(result.isUserInfoRequired ?? false)
          setShowConfirmationModal(true)
        } else {
          setIsLoading(false)
          handleMessage(errorMessage, 'error')
        }
      } catch (e) {
        setIsLoading(false)
        await handleError(e, errorMessage)
      }
    } else {
      setIsLoading(false)
      handleMessage(errorMessage, 'error')
    }
  }

  const roleAgreementMap = useMemo(() => {
    const map = new Map<
      string,
      {
        templateId: string
        agreementDocuments: AgreementDocumentSelectOption[]
      }
    >()
    roleAgreementDocuments?.forEach((roleAgreementDocument) => {
      map.set(roleAgreementDocument.roleName, {
        templateId: roleAgreementDocument.agreementTemplateId ?? '',
        agreementDocuments: roleAgreementDocument.agreementDocuments,
      })
    })
    return map
  }, [roleAgreementDocuments])

  const rolesMap = useMemo<
    Map<string /** role.roleName */, RoleAgreementDocumentSelectOption>
  >(() => {
    const map = new Map<string, RoleAgreementDocumentSelectOption>()
    roleAgreementDocuments?.forEach((roleAgreementDocument) => {
      map.set(roleAgreementDocument.roleName, roleAgreementDocument)
    })
    return map
  }, [roleAgreementDocuments])

  const agreementDocuments = useMemo(() => {
    return roleAgreementMap.get(role?.roleName ?? '')?.agreementDocuments ?? []
  }, [roleAgreementMap, role])

  useEffect(() => {
    if (!!agreementTemplateId) {
      setAgreementTemplateId(
        roleAgreementMap.get(role?.roleName ?? '')?.templateId ?? ''
      )
      if (
        previousAgreementTemplateId !== agreementTemplateId ||
        previousAgreementDocumentId !== agreementDocumentId
      ) {
        setAgreementTemplateFormFields(undefined)
        setLoadingFields(true)
      }
    }
  }, [
    agreementTemplateId,
    roleAgreementMap,
    role,
    agreementDocumentId,
    previousAgreementTemplateId,
    previousAgreementDocumentId,
  ])

  const agreementDocumentsIsEmpty = useMemo(() => {
    return !!agreementDocuments && agreementDocuments.length === 0
  }, [agreementDocuments])

  const rolesIsEmpty = useMemo(() => {
    return !!roleAgreementDocuments && roleAgreementDocuments.length === 0
  }, [roleAgreementDocuments])

  const fetchRoleAndAgreementTemplatesErrorMessage = t(
    'Team.TeamMemberForm. fetchRoleAndAgreementTemplatesErrorMessage',
    `Unknown error occurred retrieving agreement options.`
  )

  const fetchRoleAndAgreementTemplates = async () => {
    try {
      const result = await fetchTeamInviteOptions()
      setRoleAgreementDocuments(result.roleAgreementDocuments)
      setRoleEndDate(result.defaultRoleValidToDate)
      const academicYearsFilterOptions = buildAcademicYearsFilterOptions(
        result.academicYears ?? []
      )
      setAcademicYears(academicYearsFilterOptions)
      setCommunitySelectOptions(result.communities ?? [])
      setUpdateStandardizedFieldStates(true)
      setLoadRoleAgreementDocuments(false)
    } catch (err) {
      await handleError(err, fetchRoleAndAgreementTemplatesErrorMessage)
    }
  }

  const { triggerFetch: triggerFetchInviteTeamMemberOptions } =
    useLoadingContext({
      asyncFunction: fetchRoleAndAgreementTemplates,
      loadingId: fetchInviteTeamMemberOptionsLoadingId,
    })

  useMountEffect(triggerFetchInviteTeamMemberOptions)

  const academicYearDefaultMessage = t(
    'Team.TeamMemberForm.AcademicYearDefaultMessage',
    'Select an Academic Year'
  )
  const years = academicYears
    ? [{ year: 0, name: academicYearDefaultMessage }, ...academicYears]
    : []
  const academicYearOptions = years.map(({ year, name }) => {
    return { name, id: year }
  })

  const isValidAcademicYear =
    selectedAcademicYear.year !== 0 && selectedAcademicYear.name !== ''

  const fetchAgreementTemplateFieldsErrorMessage = t(
    'Team.AddTeamMemberForm.AgreementTemplateFieldsErrorMessage',
    `Error while loading agreement template form fields.`
  )

  useEffect(() => {
    if (!!agreementTemplateId && !!agreementDocumentId) {
      const fetchAgreementTemplateFields = async () => {
        try {
          const result = await fetchAgreementTemplateFormFields(
            agreementTemplateId,
            agreementDocumentId
          )

          const formRecipients = result.formRecipients
          const formData = result.formData ?? []

          setAgreementTemplateFormFields({
            formRecipients,
            formData,
          })
          setLoadingFields(false)
        } catch (err) {
          await handleError(err, fetchAgreementTemplateFieldsErrorMessage)
        }
      }
      fetchAgreementTemplateFields()
    }
  }, [
    agreementTemplateId,
    agreementDocumentId,
    fetchAgreementTemplateFieldsErrorMessage,
    handleError,
  ])

  const formRecipients = useMemo(() => {
    return (
      agreementTemplateFormFields &&
      agreementTemplateFormFields.formRecipients?.filter((it) => {
        let filterBy = role?.roleName ?? ''
        if (!!role && new RegExp('Director', 'i').test(role.roleName)) {
          filterBy = 'Director'
        }
        return !new RegExp(filterBy, 'i').test(it.label)
      })
    )
  }, [agreementTemplateFormFields, role])

  const formRecipientStates = useMemo(() => {
    return formRecipients && formRecipients
      ? formRecipients.map((recipient) => {
          return {
            label: `${recipient.label}`,
            state: {
              state: recipient.email,
              isValid: {
                input: true,
                maxLength: true,
                minLength: true,
                email: true,
              },
              required: true,
            },
          }
        })
      : undefined
  }, [formRecipients])

  /**
   * Checks each agreement template form field to verify its displayName and
   * inputName do not match the standardized fields names to prevent duplicate
   * fields from displaying
   */
  const isStandardizedField = (field: AgreementTemplateFormField) => {
    const comparisonFields = Object.keys(StandardizedFieldNames)
    const spaceRemovedDisplayName = field.displayName.split(' ').join('')
    const shouldNotInclude = [
      field.displayName,
      spaceRemovedDisplayName,
      field.inputName,
    ]

    // Verify the field matches all or part of the condition.
    const isPartialOrFullMatch = comparisonFields.some((field) =>
      shouldNotInclude.some((condition) =>
        new RegExp(field, 'i').test(condition)
      )
    )

    return isPartialOrFullMatch
  }

  const formData = useMemo(() => {
    return (
      agreementTemplateFormFields &&
      agreementTemplateFormFields.formData?.filter(
        (it) => !isStandardizedField(it)
      )
    )
  }, [agreementTemplateFormFields])

  const standardizedFieldsOfFormFields = useMemo(() => {
    return agreementTemplateFormFields?.formData?.filter((it) =>
      isStandardizedField(it)
    )
  }, [agreementTemplateFormFields])

  const dataStates = useMemo(() => {
    return formData
      ? formData.map((field) => {
          // Determine starting value of field by type
          let state = ''
          switch (field.inputType) {
            case AgreementTemplateFormFieldInputType.Date:
              state = dateToDashString(new Date())
              break
            case AgreementTemplateFormFieldInputType.Dropdown:
              state = !!field.dropdownSelectOptions?.[0]
                ? field.dropdownSelectOptions[0]
                : ''
              break
            case AgreementTemplateFormFieldInputType.Text:
              state = field.defaultValue
          }

          return {
            label: !!field.displayName ? field.displayName : field.inputName,
            state: {
              state,
              isValid: {
                input: true,
                maxLength: true,
                minLength: true,
                email: true,
                beforeMax: true,
                afterMin: true,
              },
              required: true,
              inputType: field.inputType,
              maxLength:
                field.maxTextLength !== -1 ? field.maxTextLength : undefined,
              minLength:
                field.minTextLength !== -1 ? field.minTextLength : undefined,
            },
          }
        })
      : undefined
  }, [formData])

  const { fieldStates: recipientStates, setFieldStates: setRecipientStates } =
    useDynamicFieldStates({
      fields: formRecipientStates,
      isInitialLoad: isInitialRecipientLoad,
      setIsInitialLoad: setIsInitialRecipientLoad,
    })

  const { fieldStates: formDataStates, setFieldStates: setFormDataStates } =
    useDynamicFieldStates({
      fields: dataStates,
      isInitialLoad: isInitialFormDataLoad,
      setIsInitialLoad: setIsInitialFormDataLoad,
    })

  const matchRequiredToStandardField = (
    field: AgreementTemplateFormField,
    compareTo: string
  ): boolean => {
    // We want to compare our scenarios against the StandardizedFieldNames name, say EffectiveDate = 'Effective Date'
    // But just in case we want the StandardizedFieldNames to be without spaces
    const comparisonFields = [compareTo.split(' ').join('')]

    const spaceRemovedDisplayName = field.displayName.split(' ').join('')
    const shouldInclude = [
      // We also want to check if the display name matches the field already
      // For the given example this could be EffectiveDate
      field.displayName,
      // Or if the display name with the spaces removed matches
      // For the given example this could be Effective Date => EffectiveDate
      spaceRemovedDisplayName,
      // Or if the input name matches
      // For the given example this could be EffectiveDate
      field.inputName,
    ]

    const isPartialOrFullMatch = comparisonFields.some((field) =>
      shouldInclude.some((condition) => new RegExp(field, 'i').test(condition))
    )
    // Only update if we match the field
    if (isPartialOrFullMatch) {
      return field.required
    }
    return false
  }

  const {
    fieldStates: standardizedFieldStates,
    setFieldStates: setStandardizedFieldStates,
  } = useDynamicFieldStates({
    fields: Object.values(StandardizedFieldNames).map((it) => {
      let isRequired = false
      standardizedFieldsOfFormFields?.forEach((field) => {
        const fieldRequired = matchRequiredToStandardField(field, it)
        if (fieldRequired) {
          isRequired = fieldRequired
        }
      })
      return {
        label: it,
        state: {
          state: '',
          isValid: {
            input: true,
            dateRange: true,
            email: true,
            isRequiredInSomeRolesIfValueIsEmpty: true,
            beforeMax: true,
            afterMin: true,
          },
          required:
            it === StandardizedFieldNames.EffectiveDate
              ? true
              : it === StandardizedFieldNames.EndDate
              ? false
              : isRequired, // End date is not a required field for validation, nor is c
          inputType: AgreementTemplateFormFieldInputType.Date,
        },
      }
    }),
    isInitialLoad: isInitialStandardizedFieldLoad,
    setIsInitialLoad: setIsInitialStandardizedFieldLoad,
  })

  const updateFormDataValueForParent = (
    name: string,
    state: string,
    required: boolean,
    isOverMaxLength?: boolean,
    isUnderMinLength?: boolean
  ) => {
    state = removeSpacesStartAndEndFromEmailField(name, state)

    setFormDataStates({
      ...formDataStates,
      [name]: {
        ...formDataStates[name],
        state,
        isValid: {
          ...formDataStates[name]?.isValid,
          input: true,
          maxLength: !isOverMaxLength,
          minLength: !isUnderMinLength,
          email: true,
        },
        required: required,
        validationMessage: '',
      },
    })
  }

  const resetApplicantInfo = () => {
    setApplicantInfo(initialApplicantInfo)
  }

  const handleConfirmationCancel = async () => {
    // Cancel the agreement
    await deleteAgreement(agreementId)
    // Then close the confirmation dialog
    setIsLoading(false)
    setShowConfirmationModal(false)
    resetApplicantInfo()
  }

  const validationEmailMessage = (error: string) => {
    return validationMessages(error)
  }

  useEffect(() => {
    if (!!agreementId && !bypassAgreement) {
      const getAgreementPreview = async () => {
        const fetchedAgreementPreview = await fetchAgreementPreview(agreementId)
        setAgreementPreview(fetchedAgreementPreview.pdfPreview ?? '#')
      }
      getAgreementPreview()
    }
  }, [agreementId, bypassAgreement])

  const saveTeamMemberCard = async () => {
    try {
      await inviteTeamMember({
        agreementId,
        firstName: applicantInfo.firstName,
        lastName: applicantInfo.lastName,
      })
      resetApplicantInfo()
      navigate(
        {
          pathname: '/team',
        },
        {
          state: {
            snackbarState: true,
            snackbarMessage: bypassAgreement
              ? t(
                  'Team.TeamView.SuccessMessage.InvitedTeamMember.Bypass',
                  'Team member added. An invitation will be emailed to that team member.'
                )
              : t(
                  'Team.TeamView.SuccessMessage.InvitedTeamMember.Default',
                  'Team member added. An Adobe Sign contract will be emailed to that team member.'
                ),
            snackbarSeverity: 'success',
          },
        }
      )
    } catch (e) {
      await handleError(e, errorMessage)
    }
  }

  const { triggerFetch, isLoading: isFetchingInviteTeamMember } =
    useLoadingContext({
      asyncFunction: saveTeamMemberCard,
      loadingId: inviteTeamMemberLoadingId,
    })

  const handleNextClick = () => {
    if (!isApplicantInfoRequired) {
      triggerFetch()
      return
    }

    if (isValidApplicantInfo && isApplicantInfoRequired) {
      setIsApplicantInfoRequired(false)
      return
    }
  }

  const agreementButtons = (
    <ActionButtons
      primaryButtonLabel={
        isApplicantInfoRequired
          ? ContainedButtonVariant.Next
          : ContainedButtonVariant.YesSend
      }
      secondaryButtonLabel={TextButtonVariant.NoCancel}
      disableSecondaryButton={isFetchingInviteTeamMember}
      disablePrimaryButton={
        isFetchingInviteTeamMember ||
        (!isValidApplicantInfo && isApplicantInfoRequired)
      }
      primaryButtonLoadingId={inviteTeamMemberLoadingId}
      secondaryClick={handleConfirmationCancel}
      primaryClick={handleNextClick}
    />
  )

  const headerMessage = t(
    'TeamMemberCard.ConfirmationModal.Header.AboutToSendTo.Default',
    'You are about to send a new agreement to {{signerEmail}} and invite them to your team.',
    { signerEmail }
  )

  const newHeaderMessage = isApplicantInfoRequired ? '' : headerMessage

  const agreementBypassHeaderMessage = t(
    'TeamMemberCard.ConfirmationModal.Header.AboutToSendTo.Bypass',
    'You are about to invite {{signerEmail}} to your team.',
    { signerEmail }
  )

  const confirmationBodyMessage = t(
    'TeamMemberCard.ConfirmationModal.Body.Default',
    'Please confirm that this is the action you intend to complete. It is not reversible once the agreement is sent.'
  )

  const agreementBypassConfirmationBodyMessage = t(
    'TeamMemberCard.ConfirmationModal.Body.Bypass',
    'Please confirm that this is the action you intend to complete. It is not reversible once the invitation is sent.'
  )

  const confirmationBody = (
    <Box>
      {isApplicantInfoRequired ? (
        <>
          <Typography variant="body1" component="p" align="center">
            {t(
              'TeamMemberCard.ConfirmationModal.Body.FullNameRequired',
              'Full name is required for the background check.'
            )}
          </Typography>

          <Typography variant="body2" component="p" align="center">
            {t(
              'TeamMemberCard.ConfirmationModal.Body.FullNameRequiredMessage',
              'Please enter the first and last name of the applicant to proceed with the Invitation process.'
            )}
          </Typography>

          <Box
            sx={{
              marginTop: theme.spacing(2),
              display: 'flex',
              gap: 1,
              flexDirection: 'column',
            }}
          >
            <TextField
              fullWidth
              id="firstName"
              label={t(
                'TeamMemberCard.ConfirmationModal.Body.FirstName',
                'First Name *'
              )}
              variant="filled"
              value={applicantInfo.firstName}
              onChange={(e) =>
                setApplicantInfo({
                  ...applicantInfo,
                  firstName: e.target.value,
                })
              }
              helperText={
                !applicantInfo.firstName
                  ? t(
                      'TeamMemberCard.ConfirmationModal.Body.FirstName.RequiredMessage',
                      'First Name cannot be empty'
                    )
                  : null
              }
            />
            <TextField
              fullWidth
              id="lastName"
              label={t(
                'TeamMemberCard.ConfirmationModal.Body.LastName',
                'Last Name *'
              )}
              variant="filled"
              value={applicantInfo.lastName}
              onChange={(e) =>
                setApplicantInfo({ ...applicantInfo, lastName: e.target.value })
              }
              helperText={
                !applicantInfo.lastName
                  ? t(
                      'TeamMemberCard.ConfirmationModal.Body.LastName.RequiredMessage',
                      'Last Name cannot be empty'
                    )
                  : null
              }
            />
          </Box>
        </>
      ) : (
        <Typography variant="body1" component="p" align="center">
          {bypassAgreement
            ? agreementBypassConfirmationBodyMessage
            : confirmationBodyMessage}
          &nbsp;
          {!!agreementPreview && !bypassAgreement && (
            <PublicUrlLink
              target="_blank"
              rel="noopener noreferrer"
              href={agreementPreview}
            >
              {t(
                'TeamMemberCard.ConfirmationModal.PreviewAgreement',
                'Preview the Agreement.'
              )}
            </PublicUrlLink>
          )}
        </Typography>
      )}
    </Box>
  )

  const handleAgreementConfirmationSubmit = (
    event: React.FormEvent<HTMLDivElement>
  ) => {
    event.preventDefault()
    triggerFetch()
  }

  const agreementConfirmationProps = {
    isOpen: showConfirmationModal,
    dialogTitle: bypassAgreement
      ? agreementBypassHeaderMessage
      : newHeaderMessage,
    dialogContent: confirmationBody,
    dialogActions: agreementButtons,
    handleFormSubmit: handleAgreementConfirmationSubmit,
    ...props,
  }
  const effectiveDateField = 'EffectiveDate'
  useEffect(() => {
    if (
      (roleChanged || agreementTemplateChanged) &&
      !!agreementTemplateFormFields
    ) {
      const formData = agreementTemplateFormFields.formData ?? []
      const fieldsToExclude: string[] = [
        StandardizedFieldNames.SignerEmail,
        StandardizedFieldNames.ExpirationDate,
        effectiveDateField,
      ]

      const fieldNames = formData
        .map((fieldName) =>
          !!fieldName.displayName ? fieldName.displayName : fieldName.inputName
        )
        .filter((fieldName) => !fieldsToExclude.includes(fieldName))

      setFormDataFieldNames(fieldNames)

      setRecipientFieldNames(
        agreementTemplateFormFields.formRecipients?.map(
          (recipient) => `${recipient.label}`
        ) ?? []
      )
      setUpdateStandardizedFieldStates(true)
      setRoleChanged(false)
      setAgreementTemplateChanged(false)
    }
  }, [agreementTemplateFormFields, roleChanged, agreementTemplateChanged])

  useEffect(() => {
    if (updateStandardizedFieldStates) {
      let stateObject = standardizedFieldStates
      // Set the required values first
      Object.values(StandardizedFieldNames).forEach((it) => {
        let isRequired = false
        standardizedFieldsOfFormFields?.forEach((field) => {
          const fieldRequired = matchRequiredToStandardField(field, it)
          if (fieldRequired) {
            isRequired = fieldRequired
          }
        })
        stateObject = {
          ...stateObject,
          [it]: {
            ...stateObject[it],
            required: isRequired || it === StandardizedFieldNames.EffectiveDate, // End date is not a required field for validation, nor is community
          },
        }
      })

      const { [StandardizedFieldNames.EndDate]: endDate } =
        standardizedFieldStates

      const { [StandardizedFieldNames.EndDate]: stateObjectEndDate } =
        stateObject

      const newStandardizedFieldStates = {
        ...standardizedFieldStates,

        [StandardizedFieldNames.EndDate]: {
          ...endDate,
          required: stateObjectEndDate.required,
          state:
            requiredEndDate || stateObjectEndDate.required
              ? dateToDashString(
                  reinterpretYearMonthDayAsLocalTime(roleEndDate)
                )
              : '',
        },
      }
      // Then set all the states to default values where applicable
      setStandardizedFieldStates(newStandardizedFieldStates)
      setUpdateStandardizedFieldStates(false)
    }
  }, [
    updateStandardizedFieldStates,
    setStandardizedFieldStates,
    standardizedFieldStates,
    requiredEndDate,
    roleEndDate,
    standardizedFieldsOfFormFields,
  ])

  if (loadRoleAgreementDocuments) {
    return <LoadingProgress />
  }

  const isCommunityIncluded = agreementTemplateFormFields?.formData?.some(
    (field) =>
      field.inputName === StandardizedFieldNames.CommunityId ||
      field.inputName === StandardizedFieldNames.CommunityName ||
      field.inputName === StandardizedFieldNames.Community
  )
  const isEffectiveDateValid = !!effectiveDate
  return (
    <>
      {((!showOnDesktop && !showConfirmationModal) || showOnDesktop) && (
        <Card
          sx={{
            maxWidth: 1200,
            padding: theme.spacing(3, 4),
            color: theme.palette.primary.main,
            [theme.breakpoints.down('sm')]: {
              padding: theme.spacing(3, 4),
            },
          }}
        >
          <CardFormHeader
            header={
              <Header
                id="teamMemberFormHeader"
                headerName={title}
                component="h2"
                variant={HeaderVariant.Card}
              />
            }
            buttons={
              showOnDesktop ? (
                <TeamMemberButtons
                  handleNext={handleNext}
                  isNextDisabled={!role || isLoading}
                />
              ) : null
            }
            headerContainerProps={{ margin: 0 }}
          />
          <Form
            noValidate
            autoComplete="off"
            aria-labelledby="teamMemberFormHeader"
          >
            <SectionOne aria-labelledby="teamMemberFormHeader">
              <TextField
                id="roleSelect"
                label={t('Team.TeamMemberForm.FormField.Role', 'Role')}
                variant="filled"
                onChange={handleRoleChange}
                error={!isRoleValid}
                sx={{
                  gridColumn: '1',
                  gridRow: '1',
                }}
                value={role?.roleName ?? ''}
                select={!rolesIsEmpty}
                disabled={rolesIsEmpty}
                helperText={
                  !isRoleValid
                    ? validationMessageMap.get(
                        TeamMemberValidationMessageTypes.Role
                      )
                    : rolesIsEmpty
                    ? validationMessageMap.get(
                        TeamMemberValidationMessageTypes.NoRoles
                      )
                    : null
                }
              >
                {!rolesIsEmpty &&
                  roleAgreementDocuments?.map((role) => (
                    <MenuItem key={role.roleKey} value={role.roleName}>
                      {role.roleName}
                    </MenuItem>
                  ))}
              </TextField>
              {!bypassAgreement && (
                <TextField
                  id="agreementTemplateSelect"
                  label={t(
                    'Team.TeamMemberForm.FormField.AgreementTemplate',
                    'Agreement Template'
                  )}
                  variant="filled"
                  onChange={handleAgreementTemplateChange}
                  error={!isAgreementDocumentValid}
                  sx={{
                    gridColumn: '2',
                    gridRow: '1',
                  }}
                  value={
                    !!agreementTemplate && !!agreementDocumentId
                      ? `${agreementTemplate}|-|${agreementDocumentId}`
                      : ''
                  }
                  select={!agreementDocumentsIsEmpty}
                  disabled={!role}
                  helperText={
                    !isAgreementDocumentValid
                      ? validationMessageMap.get(
                          TeamMemberValidationMessageTypes.AgreementTemplate
                        )
                      : null
                  }
                >
                  {!agreementDocumentsIsEmpty ? (
                    agreementDocuments.map((document) => (
                      <MenuItem
                        key={document.agreementDocumentId}
                        value={`${document.agreementDocumentName}|-|${document.agreementDocumentId}`}
                      >
                        {document.agreementDocumentName}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      {validationMessageMap.get(
                        TeamMemberValidationMessageTypes.NoResultsForRole
                      )}
                    </MenuItem>
                  )}
                </TextField>
              )}
            </SectionOne>
            <FormDivider />
            <CardFormHeader
              header={
                <Header
                  id="recipientEmails"
                  headerName={t(
                    'Team.TeamMemberForm.Headers.RecipientEmails',
                    'Recipient Email'
                  )}
                  component="h3"
                  variant={HeaderVariant.Subheader}
                />
              }
              headerContainerProps={{ margin: 0 }}
            />
            <SectionOne aria-labelledby="recipientEmails">
              {Object.values(recipientStates).length > 0 || bypassAgreement ? (
                <>
                  <TextField
                    id="signerEmailField"
                    label={
                      !bypassAgreement
                        ? t(
                            'Team.TeamMemberCard.FormField.SignerEmail',
                            'Signer Email'
                          )
                        : t(
                            'Team.TeamMemberCard.FormField.TeamMemberEmail',
                            'Team Member Email'
                          )
                    }
                    variant="filled"
                    value={signerEmail}
                    onChange={handleSignerEmailChange}
                    error={isFormValidated && !isSignerEmailValid}
                    helperText={
                      isFormValidated &&
                      !isSignerEmailValid &&
                      validationEmailMessage(textErrorEmail)
                    }
                  />
                </>
              ) : null}
            </SectionOne>
            <FormDivider />
            <CardFormHeader
              header={
                <Header
                  id="agreementFields"
                  headerName={t(
                    'Team.TeamMemberForm.Headers.AgreementFields',
                    'Agreement Fields'
                  )}
                  component="h4"
                  variant={HeaderVariant.Subheader}
                />
              }
              headerContainerProps={{ margin: 0 }}
            />
            {loadingFields && <LoadingProgress />}
            <SectionOne aria-labelledby="agreementFields">
              {!loadingFields && requiredEndDate && (
                <DropDown
                  id="years"
                  label={t(
                    'Team.TeamMemberForm.FormField.years',
                    'Academic Year'
                  )}
                  menuOptions={academicYearOptions}
                  defaultValue={academicYearDefaultMessage}
                  value={selectedAcademicYear.name}
                  handleSelection={(selection) => {
                    const { year, name } = academicYears?.find(
                      ({ name }) => name === selection
                    ) || { year: 0, name: '' }

                    setSelectedAcademicYear({ year, name })
                  }}
                  variant={DropDownVariant.FormField}
                  error={isFormValidated && !isValidAcademicYear}
                  helperText={
                    isFormValidated && !isValidAcademicYear
                      ? t(
                          'Team.TeamMemberForm.AcademicYearValidationMessage',
                          'Academic Year is required, please select an Academic Year.'
                        )
                      : undefined
                  }
                  formControlProps={{
                    marginTop: theme.spacing(0.8),
                    width: '100%',
                    [theme.breakpoints.down('sm')]: {
                      width: 'auto',
                      marginLeft: 0,
                      marginRight: theme.spacing(1),
                    },
                  }}
                  cssProps={{
                    padding: 0,
                    height: 'auto',
                    textAlign: 'left',
                    marginLeft: theme.spacing(1),
                    [theme.breakpoints.down('sm')]: {
                      marginLeft: 0,
                    },
                  }}
                />
              )}

              {!requiredEndDate && roleId > 0 && (
                <DateField
                  id={StandardizedFieldNames.EffectiveDate}
                  dateVariant={DateFieldVariant.EffectiveDate}
                  name={StandardizedFieldNames.EffectiveDate}
                  type="date"
                  onChangeForParent={(_, value) =>
                    setEffectiveDate(value ? createSafeDate(value) : new Date())
                  }
                  value={dateToDashString(effectiveDate)}
                  error={isFormValidated && !isEffectiveDateValid}
                  helperText={
                    isFormValidated && !isEffectiveDateValid
                      ? t(
                          'Team.TeamMemberForm.EffectiveDateValidationMessage',
                          'Effective Date is required'
                        )
                      : undefined
                  }
                />
              )}
              {!bypassAgreement && (
                <>
                  {isCommunityIncluded && (
                    <DropDown
                      id="community"
                      label={t(
                        'Team.TeamMemberForm.FormField.community',
                        'Community'
                      )}
                      menuOptions={communityOptions}
                      defaultValue={communityDefault}
                      value={selectedCommunity.name}
                      handleSelection={(selection, _, id) => {
                        setSelectedCommunity({
                          communityKey: id.toString(),
                          name: selection,
                        })
                      }}
                      variant={DropDownVariant.FormField}
                      error={isFormValidated && !isValidCommunity}
                      helperText={
                        isFormValidated && !isValidCommunity
                          ? t(
                              'Team.TeamMemberForm.CommunityValidationMessage',
                              'Community is required, please select a community.'
                            )
                          : undefined
                      }
                      formControlProps={{
                        marginTop: theme.spacing(0.8),
                        width: '100%',
                        [theme.breakpoints.down('sm')]: {
                          width: 'auto',
                          marginLeft: 0,
                          marginRight: theme.spacing(1),
                        },
                      }}
                      cssProps={{
                        padding: 0,
                        height: 'auto',
                        textAlign: 'left',
                        marginLeft: theme.spacing(1),
                        [theme.breakpoints.down('sm')]: {
                          marginLeft: 0,
                        },
                      }}
                    />
                  )}
                  <FormData
                    formData={formData}
                    formDataStates={formDataStates}
                    updateFormDataValueForParent={updateFormDataValueForParent}
                  />
                </>
              )}
            </SectionOne>

            <SectionOne>
              {showProgramsSelector && (
                <Box
                  sx={{
                    margin: 1,
                  }}
                >
                  <ProgramsCheckBoxSelector
                    handleProgramSelection={(programs) => {
                      setSelectedPrograms(programs.join(', '))
                    }}
                  />
                  {isFormValidated && !isProgramsFieldValid && (
                    <FormHelperText
                      sx={{
                        color: theme.palette.error.main,
                      }}
                    >
                      {t(
                        'Team.TeamMemberForm.ProgramsValidationMessage',
                        'Programs are required, please select at least one program.'
                      )}
                    </FormHelperText>
                  )}
                </Box>
              )}
            </SectionOne>
          </Form>
        </Card>
      )}
      {!showOnDesktop && !showConfirmationModal ? (
        <TeamMemberButtons
          handleNext={handleNext}
          isNextDisabled={!role || isLoading}
        />
      ) : null}
      <ConfirmationModal {...agreementConfirmationProps} />
    </>
  )
}

export default TeamMemberCard
